import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import Papa from 'papaparse';


const SurveyResponses = ({ surveyId }) => {
  const { getAuthHeaders } = useAuth();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);

  useEffect(() => {
    if (surveyId) {
      fetchResponses();
      fetchSurvey();
    }
  }, [surveyId]);

  const fetchResponses = async () => {
    try {
      const response = await axios.get(`${API_URL}/surveys/surveys/${surveyId}/responses`, {
        headers: getAuthHeaders()
      });
      setResponses(response.data.responses);
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to fetch survey responses');
    } finally {
      setLoading(false);
    }
  };

  const fetchSurvey = async () => {
    try {
      const response = await axios.get(`${API_URL}/surveys/surveys/${surveyId}`, {
        headers: getAuthHeaders()
      });
      setSurvey(response.data.survey);
    } catch (error) {
      console.error('Error fetching survey:', error);
    }
  };

  const formatAnswer = (answer) => {
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    if (typeof answer === 'object') {
      return JSON.stringify(answer);
    }
    return String(answer);
  };

  const getQuestionTitle = (questionName) => {
    if (!survey) return questionName;
    const question = survey.questions.find(q => q.name === questionName);
    return question ? question.title : questionName;
  };

  const getQuestionSection = (questionName) => {
    if (!survey) return '';
    const question = survey.questions.find(q => q.name === questionName);
    return question ? (question.section || 'General') : '';
  };

  const exportToCSV = () => {
    if (!responses.length || !survey) {
      toast.error('No data to export');
      return;
    }

    try {
      // Get all unique question names from responses
      const allQuestionNames = new Set();
      responses.forEach(response => {
        response.responses.forEach(resp => {
          allQuestionNames.add(resp.questionName);
        });
      });
      
      // Create a map of question names to titles with numbering
      const questionMap = new Map();
      const questionTitles = new Set();
      
      // First, add questions from survey with numbering and sections
      survey.questions.forEach((question, index) => {
        const section = question.section || 'General';
        const numberedTitle = `Q${index + 1} (${section}): ${question.title}`;
        questionMap.set(question.name, numberedTitle);
        questionTitles.add(question.title);
      });
      
      // Add any missing questions from responses
      allQuestionNames.forEach(questionName => {
        if (!questionMap.has(questionName)) {
          // Check if this question title already exists (to avoid duplicates)
          const existingTitle = Array.from(questionTitles).find(title => 
            title === questionName || title.replace('?', '') === questionName.replace('?', '')
          );
          
          if (existingTitle) {
            // Use the existing title format
            const existingQuestion = survey.questions.find(q => q.title === existingTitle);
            if (existingQuestion) {
              questionMap.set(questionName, questionMap.get(existingQuestion.name));
            } else {
              questionMap.set(questionName, `Q${questionMap.size + 1}: ${questionName}`);
            }
          } else {
            questionMap.set(questionName, `Q${questionMap.size + 1}: ${questionName}`);
          }
        }
      });
      
      // Prepare data for CSV export using PapaParse
      const csvData = [];
      
      // Add data rows
      responses.forEach(response => {
        const row = {
          'User Name': `${response.userId?.firstName || ''} ${response.userId?.lastName || ''}`.trim(),
          'User Email': response.userId?.email || '',
          'Submission Date': new Date(response.submittedAt).toLocaleDateString()
        };

        // Add answers for each question (from both survey and responses)
        questionMap.forEach((questionTitle, questionName) => {
          const answer = response.responses.find(resp => resp.questionName === questionName);
          if (answer) {
            row[questionTitle] = formatAnswer(answer.answer);
          } else {
            row[questionTitle] = '';
          }
        });

        csvData.push(row);
      });

      // Generate CSV using PapaParse
      const csvContent = Papa.unparse(csvData, {
        header: true,
        quotes: true,
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        newline: '\n'
      });

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${survey.title}_responses_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" gutterBottom>
          Survey Responses
        </Typography>
        {responses.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            color="primary"
          >
            Export to CSV
          </Button>
        )}
      </Box>
      
      {survey && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {survey.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {survey.description}
            </Typography>
            <Chip
              label={`${responses.length} responses`}
              color="primary"
              size="small"
            />
          </CardContent>
        </Card>
      )}

      {responses.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No responses yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Responses will appear here once users complete the survey
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {responses.map((response, index) => (
            <Grid item xs={12} key={response._id}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <PersonIcon color="primary" />
                    <Box flex={1}>
                      <Typography variant="subtitle1">
                        {response.userId?.firstName} {response.userId?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {response.userId?.email}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(response.submittedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Section</strong></TableCell>
                          <TableCell><strong>Question</strong></TableCell>
                          <TableCell><strong>Answer</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {response.responses.map((resp, respIndex) => (
                          <TableRow key={respIndex}>
                            <TableCell>
                              <Chip 
                                label={getQuestionSection(resp.questionName)} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {getQuestionTitle(resp.questionName)}
                            </TableCell>
                            <TableCell>
                              {formatAnswer(resp.answer)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SurveyResponses; 